import { useId, useState, type ChangeEvent } from "react";
import { VoiceableInput, type VoiceRecordingState } from "./VoiceableInput";
import { designEditValue } from "../shared/design-inputs";
import {
  IMAGE_UPLOAD_MIME_TYPES,
  BRAND_UPLOAD_MIME_TYPES,
  uploadAttachmentBatch,
  uploadIssueLocaleKey,
  type UploadFailure
} from "./attachment-upload";

/**
 * Structured questionnaire field pieces (fix-q2-details, Pavel 2026-08-06).
 *
 * F65: a follow-up prose field is answered by VOICE exactly like a main
 * question - mic orb, live transcript into the field, the recording archived.
 * The recording's attachment id lives inside the answer value under
 * `<field>_recording` (the repeat-entry pattern), so both writes of a voice
 * take travel through the ordinary debounced answer save and the F43
 * conflict reconciliation - no second mechanism.
 */

type ValueUpdate = (value: Record<string, unknown>) => void;

interface FieldContext {
  value: Record<string, unknown>;
  update: ValueUpdate;
  t(key: string): string;
}

function stringValue(value: unknown): string {
  return typeof value === "string" ? value : "";
}

export function VoiceTextField(props: FieldContext & {
  field: string;
  scope: "q1_voice" | "q2_voice";
  csrfToken: string;
  onRecordingStateChange(state: VoiceRecordingState): void;
}) {
  const label = props.t(`questionnaire.fields.${props.field}`);
  const recordingKey = `${props.field}_recording`;
  return (
    <div className="voice-followup-field">
      <span className="choice-field-label">{label}</span>
      <VoiceableInput
        mini
        scope={props.scope}
        value={stringValue(props.value[props.field])}
        attachmentId={stringValue(props.value[recordingKey]) || undefined}
        ariaLabel={label}
        csrfToken={props.csrfToken}
        t={props.t}
        onRecordingStateChange={props.onRecordingStateChange}
        onTextChange={(text) => props.update({ ...props.value, [props.field]: text })}
        onAttachmentReady={(attachment) => props.update({
          ...props.value,
          ...(attachment.transcript && !stringValue(props.value[props.field]).trim()
            ? { [props.field]: attachment.transcript }
            : {}),
          [recordingKey]: attachment.attachmentId
        })}
        onAttachmentClear={() => {
          const next = { ...props.value };
          delete next[recordingKey];
          props.update(next);
        }}
      />
    </div>
  );
}

export function LinkListField(props: FieldContext & { fieldKey: string }) {
  const inputId = useId();
  const [link, setLink] = useState("");
  const links = Array.isArray(props.value[props.fieldKey]) ? (props.value[props.fieldKey] as unknown[]).map(String) : [];
  const linkValid = /^https?:\/\/\S+\.\S+/.test(link.trim());

  function addLink() {
    const trimmed = link.trim();
    if (!linkValid || links.includes(trimmed)) return;
    props.update({ ...props.value, [props.fieldKey]: [...links, trimmed] });
    setLink("");
  }

  return (
    <div className="proof-links">
      <label htmlFor={inputId}>{props.t(`questionnaire.fields.${props.fieldKey}`)}</label>
      {links.length > 0 && (
        <ul className="proof-item-list">
          {links.map((item) => (
            <li key={item}>
              <a className="quiet-link break-text" href={item} target="_blank" rel="noreferrer">{item}</a>
              <button
                className="quiet-link proof-remove"
                type="button"
                onClick={() => props.update({ ...props.value, [props.fieldKey]: links.filter((entry) => entry !== item) })}
              >
                {props.t("questionnaire.links.remove")}
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="proof-link-row">
        <input
          id={inputId}
          type="url"
          inputMode="url"
          placeholder="https://"
          value={link}
          onChange={(event) => setLink(event.target.value)}
          onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); addLink(); } }}
        />
        <button className="secondary-button compact-button" type="button" disabled={!linkValid} onClick={addLink}>
          {props.t("questionnaire.links.add")}
        </button>
      </div>
    </div>
  );
}

function previewableImage(name: string | null): boolean {
  return Boolean(name && /\.(png|jpe?g|webp)$/i.test(name));
}

export function FileListField(props: FieldContext & {
  csrfToken: string;
  scope: string;
  labelPrefix: string;
  accept?: readonly string[];
  maxFiles?: number;
  multiple?: boolean;
  preview?: boolean;
}) {
  const accept = props.accept ?? IMAGE_UPLOAD_MIME_TYPES;
  const maxFiles = props.maxFiles ?? Infinity;
  // F84 (Pavel 2026-08-06): every field that ACCEPTS several files also lets
  // the client PICK several at once. The only single-select field left is one
  // capped at a single file (the profile photo); "přidat další" one by one is
  // never the default again.
  const multiple = props.multiple ?? maxFiles > 1;
  const [uploading, setUploading] = useState(false);
  const [failures, setFailures] = useState<UploadFailure[]>([]);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const files = Array.isArray(props.value.attachments)
    ? (props.value.attachments as Array<Record<string, unknown>>).filter((item) => typeof item?.id === "string")
    : [];

  // Every failure names its reason PER FILE (fix-q1flow F44, F84): one refused
  // file never sinks the batch - the others upload, and the refused ones come
  // back as "name: reason" lines instead of one anonymous message that hides
  // which file it was about. The loop itself lives in uploadAttachmentBatch,
  // shared by every multi-file field.
  async function upload(event: ChangeEvent<HTMLInputElement>) {
    const picked = [...(event.target.files ?? [])].slice(0, Math.max(0, maxFiles - files.length));
    event.target.value = "";
    if (picked.length === 0) return;
    setUploading(true);
    setFailures([]);
    const result = await uploadAttachmentBatch(picked, {
      scope: props.scope,
      csrfToken: props.csrfToken,
      accepted: accept,
      onProgress: (done, total) => setProgress({ done, total })
    });
    if (result.added.length > 0) {
      props.update({ ...props.value, attachments: [...files, ...result.added] });
    }
    setFailures(result.failures);
    setProgress(null);
    setUploading(false);
  }

  function removeFile(id: string) {
    props.update({ ...props.value, attachments: files.filter((item) => String(item.id) !== id) });
  }

  return (
    <div className="proof-files">
      {files.length > 0 && (
        <ul className={`proof-item-list${props.preview ? " proof-thumb-list" : ""}`}>
          {files.map((item) => {
            const name = typeof item.name === "string" ? item.name : null;
            return (
              <li key={String(item.id)}>
                {props.preview && previewableImage(name) && (
                  <img
                    className="proof-thumb"
                    src={`/api/attachments/${encodeURIComponent(String(item.id))}`}
                    alt={name ?? props.t(`${props.labelPrefix}.file`)}
                    loading="lazy"
                  />
                )}
                <span className="break-text">{name ?? props.t(`${props.labelPrefix}.file`)}</span>
                <button className="quiet-link proof-remove" type="button" onClick={() => removeFile(String(item.id))}>
                  {props.t(`${props.labelPrefix}.remove`)}
                </button>
              </li>
            );
          })}
        </ul>
      )}
      {files.length < maxFiles && (
        <label className="screenshot-drop proof-drop">
          <span className="screenshot-drop-icon" aria-hidden="true" />
          <span className="screenshot-drop-copy">
            <strong>{props.t(`${props.labelPrefix}.upload`)}</strong>
            <small>{props.t(`${props.labelPrefix}.uploadHint`)}</small>
          </span>
          <input
            type="file"
            accept={accept.join(",")}
            multiple={multiple}
            disabled={uploading}
            onChange={(event) => void upload(event)}
          />
        </label>
      )}
      {uploading && progress && (
        <p className="message proof-upload-progress" role="status">
          {props.t("questionnaire.upload.progress")
            .replace("{done}", String(progress.done))
            .replace("{total}", String(progress.total))}
        </p>
      )}
      {failures.length > 0 && (
        <ul className="proof-upload-failures" role="alert">
          {failures.map((failure, index) => (
            <li key={`${failure.name}-${index}`} className="message error-message">
              <strong className="break-text">{failure.name}</strong>: {props.t(uploadIssueLocaleKey(failure.issue))}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * F76: the design question collects structure, not three loose text boxes -
 * own links, uploaded references (image/PDF), a spoken or written feel, and
 * example sites. Every write persists the NEW value shape; a legacy answer
 * (style_source/inspiration) is migrated on the first edit by
 * designEditValue, URLs into the lists, prose into the description.
 */
export function DesignInputsField(props: {
  value: Record<string, unknown>;
  update: ValueUpdate;
  csrfToken: string;
  scope: "q1_voice" | "q2_voice";
  t(key: string): string;
  onRecordingStateChange(state: VoiceRecordingState): void;
}) {
  const editValue = designEditValue(props.value);
  const commit: ValueUpdate = (next) => props.update(next);
  const deferred = editValue.defer_to_team === true;
  return (
    <div className="structured-fields design-inputs-field">
      {/* F87: an explicit "leave it to the team" answer. It completes the
          question on its own; the fields below stay open so the client can
          still add references or change their mind. */}
      <div className="design-defer">
        <button
          type="button"
          className={`secondary-button compact-button design-defer-toggle${deferred ? " active" : ""}`}
          aria-pressed={deferred}
          onClick={() => {
            const next = { ...editValue };
            if (deferred) delete next.defer_to_team;
            else next.defer_to_team = true;
            commit(next);
          }}
        >
          {deferred ? "✓ " : ""}{props.t("questionnaire.design.deferButton")}
        </button>
        {deferred && <p className="design-defer-note">{props.t("questionnaire.design.deferNote")}</p>}
      </div>
      <LinkListField fieldKey="own_links" value={editValue} update={commit} t={props.t} />
      <FileListField
        value={editValue}
        update={commit}
        t={props.t}
        csrfToken={props.csrfToken}
        scope="brand_material"
        labelPrefix="questionnaire.design"
        accept={BRAND_UPLOAD_MIME_TYPES}
        multiple
        preview
      />
      <VoiceTextField
        field="design_feel"
        value={editValue}
        update={commit}
        t={props.t}
        scope={props.scope}
        csrfToken={props.csrfToken}
        onRecordingStateChange={props.onRecordingStateChange}
      />
      <LinkListField fieldKey="example_links" value={editValue} update={commit} t={props.t} />
    </div>
  );
}
